import * as React from "react";
import { ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { NativeImage } from "@/components/native/NativeImage";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import type { MediaViewerGestureLock } from "@/lib/native/mediaViewerGestureLock";

type Point = { x: number; y: number };

type ZoomPreset = "0%" | "50%" | "100%";

const ZOOM_CYCLE: ZoomPreset[] = ["0%", "50%", "100%"];

const ZOOM_PRESET_SCALE: Record<ZoomPreset, number> = {
  "0%": 1,
  "50%": 2,
  "100%": 3,
};

function scaleToPreset(scale: number): ZoomPreset {
  if (scale <= 1.05) return "0%";
  if (scale <= 2.25) return "50%";
  return "100%";
}

function buildTransform(translate: Point, scale: number) {
  return `translate(calc(-50% + ${translate.x}px), calc(-50% + ${translate.y}px)) scale(${scale})`;
}

/**
 * Full-screen/lightbox image zoom via one control + double-tap (cycles 0% → 50% → 100%).
 * Pan with one finger when zoomed in.
 */
export function ZoomableImage({
  src,
  alt,
  className,
  imgClassName,
  onLoad,
  onError,
  draggable = false,
  gestureLockRef,
  showZoomControls = true,
  onZoomedChange,
}: {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
  draggable?: boolean;
  gestureLockRef?: React.RefObject<MediaViewerGestureLock>;
  showZoomControls?: boolean;
  onZoomedChange?: (zoomed: boolean) => void;
}) {
  const { isNative } = useNativePlatform();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const translateRef = React.useRef<Point>({ x: 0, y: 0 });
  const scaleRef = React.useRef(1);
  const panStart = React.useRef<{
    x: number;
    y: number;
    translate: Point;
  } | null>(null);
  const panningRef = React.useRef(false);
  const lastTapRef = React.useRef<{ t: number; x: number; y: number } | null>(
    null,
  );
  const didPanThisGestureRef = React.useRef(false);
  const suppressClickUntilRef = React.useRef(0);
  const lastZoomCycleAtRef = React.useRef(0);

  const [scale, setScale] = React.useState(1);
  const [translate, setTranslate] = React.useState<Point>({ x: 0, y: 0 });
  const [panning, setPanning] = React.useState(false);

  const activePreset = scaleToPreset(scale);
  const isZoomed = scale > 1.01;

  const applyTransform = React.useCallback((nextTranslate: Point, nextScale: number) => {
    const el = imgRef.current;
    if (!el) return;
    el.style.transform = buildTransform(nextTranslate, nextScale);
  }, []);

  const setPanningState = React.useCallback((next: boolean) => {
    panningRef.current = next;
    setPanning(next);
  }, []);

  const syncGestureLock = React.useCallback(() => {
    const zoomed = scaleRef.current > 1.01 || panningRef.current;
    if (gestureLockRef) {
      gestureLockRef.current.blockSwipes = zoomed;
    }
    onZoomedChange?.(scaleRef.current > 1.01);
  }, [gestureLockRef, onZoomedChange]);

  const reset = React.useCallback(() => {
    scaleRef.current = 1;
    translateRef.current = { x: 0, y: 0 };
    panStart.current = null;
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setPanningState(false);
    applyTransform({ x: 0, y: 0 }, 1);
  }, [applyTransform, setPanningState]);

  React.useEffect(() => {
    reset();
  }, [src, reset]);

  React.useEffect(() => {
    scaleRef.current = scale;
    applyTransform(translateRef.current, scale);
  }, [scale, applyTransform]);

  React.useEffect(() => {
    translateRef.current = translate;
    if (!panningRef.current) {
      applyTransform(translate, scaleRef.current);
    }
  }, [translate, applyTransform]);

  React.useEffect(() => {
    syncGestureLock();
    return () => {
      if (gestureLockRef) gestureLockRef.current.blockSwipes = false;
      onZoomedChange?.(false);
    };
  }, [gestureLockRef, scale, panning, syncGestureLock, onZoomedChange]);

  const beginPan = React.useCallback(
    (clientX: number, clientY: number) => {
      if (scaleRef.current <= 1.01) return false;
      didPanThisGestureRef.current = false;
      panStart.current = {
        x: clientX,
        y: clientY,
        translate: { ...translateRef.current },
      };
      setPanningState(true);
      syncGestureLock();
      return true;
    },
    [setPanningState, syncGestureLock],
  );

  const movePan = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!panStart.current || scaleRef.current <= 1.01) return;
      const dx = clientX - panStart.current.x;
      const dy = clientY - panStart.current.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        didPanThisGestureRef.current = true;
      }
      const next = {
        x: panStart.current.translate.x + dx,
        y: panStart.current.translate.y + dy,
      };
      translateRef.current = next;
      applyTransform(next, scaleRef.current);
    },
    [applyTransform],
  );

  const endPan = React.useCallback(() => {
    if (!panStart.current) return;
    panStart.current = null;
    setTranslate({ ...translateRef.current });
    setPanningState(false);
    syncGestureLock();
  }, [setPanningState, syncGestureLock]);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      // Native touch pan uses dedicated listeners (non-passive) for smooth tracking.
      if (isNative && e.pointerType === "touch") return;
      const el = containerRef.current;
      if (!el) return;
      if (!beginPan(e.clientX, e.clientY)) return;
      el.setPointerCapture(e.pointerId);
      e.preventDefault();
      e.stopPropagation();
    },
    [beginPan, isNative],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (!panStart.current) return;
      if (isNative && e.pointerType === "touch") return;
      movePan(e.clientX, e.clientY);
      e.preventDefault();
      e.stopPropagation();
    },
    [movePan, isNative],
  );

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      if (isNative && e.pointerType === "touch") return;
      if (!panStart.current) return;
      endPan();
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    },
    [endPan, isNative],
  );

  // Capacitor only — passive:false touchmove for 1:1 pan without fighting carousel.
  React.useEffect(() => {
    if (!isNative) return;
    const stage = containerRef.current;
    if (!stage) return;

    const onTouchStart = (e: TouchEvent) => {
      if (scaleRef.current <= 1.01 || e.touches.length !== 1) return;
      if (!beginPan(e.touches[0].clientX, e.touches[0].clientY)) return;
      e.stopPropagation();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!panStart.current || scaleRef.current <= 1.01 || e.touches.length !== 1) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      movePan(e.touches[0].clientX, e.touches[0].clientY);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!panStart.current) return;
      e.stopPropagation();
      endPan();
    };

    stage.addEventListener("touchstart", onTouchStart, { passive: true });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    stage.addEventListener("touchend", onTouchEnd, { passive: true });
    stage.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      stage.removeEventListener("touchstart", onTouchStart);
      stage.removeEventListener("touchmove", onTouchMove);
      stage.removeEventListener("touchend", onTouchEnd);
      stage.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isNative, beginPan, movePan, endPan]);

  const applyPreset = React.useCallback(
    (preset: ZoomPreset) => {
      const nextScale = ZOOM_PRESET_SCALE[preset];
      scaleRef.current = nextScale;
      setScale(nextScale);
      if (nextScale <= 1.01) {
        translateRef.current = { x: 0, y: 0 };
        setTranslate({ x: 0, y: 0 });
        applyTransform({ x: 0, y: 0 }, nextScale);
      } else {
        applyTransform(translateRef.current, nextScale);
      }
    },
    [applyTransform],
  );

  const cycleZoom = React.useCallback(() => {
    const now = Date.now();
    if (now - lastZoomCycleAtRef.current < 420) return;
    lastZoomCycleAtRef.current = now;

    const current = scaleToPreset(scaleRef.current);
    const idx = ZOOM_CYCLE.indexOf(current);
    const next = ZOOM_CYCLE[(idx + 1) % ZOOM_CYCLE.length];
    applyPreset(next);
  }, [applyPreset]);

  const tryDoubleTap = React.useCallback(
    (clientX: number, clientY: number) => {
      const now = Date.now();
      const prev = lastTapRef.current;
      lastTapRef.current = { t: now, x: clientX, y: clientY };
      if (!prev) return false;
      if (now - prev.t > 320) return false;
      if (Math.hypot(clientX - prev.x, clientY - prev.y) > 36) return false;
      lastTapRef.current = null;
      suppressClickUntilRef.current = now + 520;
      cycleZoom();
      return true;
    },
    [cycleZoom],
  );

  const onDoubleClick = React.useCallback(() => {
    // Native touch uses touchend double-tap; iOS also fires synthetic dblclick.
    if (isNative) return;
    cycleZoom();
  }, [cycleZoom, isNative]);

  // Double-tap to zoom on touch devices (dblclick does not fire reliably on mobile web).
  React.useEffect(() => {
    const stage = containerRef.current;
    if (!stage) return;

    const onTouchEnd = (e: TouchEvent) => {
      if (didPanThisGestureRef.current) {
        didPanThisGestureRef.current = false;
        return;
      }
      if (panStart.current || panningRef.current) return;
      if (e.changedTouches.length !== 1) return;
      const touch = e.changedTouches[0];
      if (tryDoubleTap(touch.clientX, touch.clientY)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onClick = (e: MouseEvent) => {
      if (Date.now() < suppressClickUntilRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    stage.addEventListener("touchend", onTouchEnd, { passive: false });
    stage.addEventListener("click", onClick, true);
    return () => {
      stage.removeEventListener("touchend", onTouchEnd);
      stage.removeEventListener("click", onClick, true);
    };
  }, [tryDoubleTap]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "native-media-viewer-image-stage relative h-full w-full overflow-hidden select-none",
        isZoomed || panning ? "touch-none" : "touch-manipulation",
        className,
      )}
      style={{
        touchAction: isZoomed || panning ? "none" : undefined,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
    >
      <NativeImage
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={draggable}
        onLoad={onLoad}
        onError={onError}
        className={cn(
          "absolute left-1/2 top-1/2 max-h-[calc(100dvh-4rem)] max-w-[calc(100vw-4rem)] object-contain",
          imgClassName,
        )}
        style={{
          transform: buildTransform(translate, scale),
          transformOrigin: "center center",
          willChange: panning ? "transform" : undefined,
        }}
      />

      {showZoomControls ? (
        <button
          type="button"
          aria-label={`Zoom ${activePreset}, tryck för nästa nivå`}
          className="pointer-events-auto absolute top-[calc(1rem+env(safe-area-inset-top,0px))] left-4 z-[10000] flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-2 text-xs font-semibold text-white touch-manipulation transition-colors hover:bg-black/70"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            cycleZoom();
          }}
        >
          <ZoomIn className="h-4 w-4 shrink-0" aria-hidden />
          <span>{activePreset}</span>
        </button>
      ) : null}
    </div>
  );
}
